import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Plus, PackagePlus, CheckCircle2, Receipt, Ban, Trash2, Printer } from "lucide-react";
import { moneyBRL } from "@/utils/money";

import AddItemModal from "./modals/AddItemModal";
import AddPecaQuickModal from "./modals/AddPecaQuickModal";
import ConfirmModal from "@/components/modals/ConfirmModal";

type OStatus = "aberta" | "em_execucao" | "aguardando_pecas" | "finalizada" | "faturada" | "cancelada";

type OS = {
  id: string;
  status: OStatus;
  total: any;
  observacaoCliente?: string | null;
  diagnostico?: string | null;
  createdAt: string;
  updatedAt: string;
  cliente?: { id: string; nome: string; documento?: string | null; telefone?: string | null; email?: string | null };
  veiculo?: { id: string; placa?: string | null; marca?: string | null; modelo?: string | null; ano?: number | null; km?: any };
  itens: Array<{
    id: string;
    tipo: "servico" | "peca";
    descricao: string;
    qtd: any;
    valorUnit: any;
    total: any;
    pecaId?: string | null;
    createdAt: string;
  }>;
  nota?: { id: string; status: string; provedor?: string | null; numero?: string | null; pdfUrl?: string | null; xmlUrl?: string | null } | null;
};

function toNumber(v: any) {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}


function statusChip(status: OStatus) {
  const base = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border";
  switch (status) {
    case "aberta":
      return <span className={`${base} bg-slate-50 text-slate-700 border-slate-200`}>Aberta</span>;
    case "em_execucao":
      return <span className={`${base} bg-blue-50 text-blue-700 border-blue-200`}>Em execução</span>;
    case "aguardando_pecas":
      return <span className={`${base} bg-amber-50 text-amber-800 border-amber-200`}>Aguardando peças</span>;
    case "finalizada":
      return <span className={`${base} bg-emerald-50 text-emerald-700 border-emerald-200`}>Finalizada</span>;
    case "faturada":
      return <span className={`${base} bg-purple-50 text-purple-700 border-purple-200`}>Faturada</span>;
    case "cancelada":
      return <span className={`${base} bg-red-50 text-red-700 border-red-200`}>Cancelada</span>;
    default:
      return <span className={`${base} bg-slate-50 text-slate-700 border-slate-200`}>{status}</span>;
  }
}




export default function OSShow() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [os, setOs] = useState<OS | null>(null);

  const [openAddItem, setOpenAddItem] = useState(false);
  const [openAddPecaQuick, setOpenAddPecaQuick] = useState(false);

  const [confirm, setConfirm] = useState<null | { type: "finalizar" | "faturar" | "cancelar"; title: string; desc: string }>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const canEdit = useMemo(() => {
    if (!os) return false;
    return os.status !== "finalizada" && os.status !== "faturada" && os.status !== "cancelada";
  }, [os]);


  function canPrint(status?: OStatus) {
    return status === "finalizada" || status === "faturada";
  }

  async function handlePrint() {
    if (!os?.id) return;

    try {
      const res = await api.get(`/api/os/${os.id}/pdf`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);

      window.open(blobUrl, "_blank", "noopener,noreferrer");

      // opcional: revoga depois de um tempinho
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao abrir PDF");
    }
  }


  const itensComTotal = useMemo(() => {
    if (!os) return [];
    return os.itens.map((it) => {
      const qtd = toNumber(it.qtd);
      const unit = toNumber(it.valorUnit);
      const totalCalc = qtd * unit;
      return { ...it, _totalCalc: totalCalc };
    });
  }, [os]);

  const totalCalcOS = useMemo(() => {
    return itensComTotal.reduce((acc, it) => acc + (it._totalCalc ?? 0), 0);
  }, [itensComTotal]);


  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/os/${id}`);
      setOs(data?.os ?? null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao carregar OS");
      navigate("/os");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function askAction(type: "finalizar" | "faturar" | "cancelar") {
    if (!os) return;

    if (type === "finalizar") {
      setConfirm({
        type,
        title: "Finalizar OS",
        desc: "Após finalizar, você não poderá alterar itens. Confirma?",
      });
    }
    if (type === "faturar") {
      setConfirm({
        type,
        title: "Faturar OS",
        desc: "A OS precisa estar FINALIZADA. Confirma faturar?",
      });
    }
    if (type === "cancelar") {
      setConfirm({
        type,
        title: "Cancelar OS",
        desc: "O cancelamento estorna o estoque das peças. Confirma?",
      });
    }
  }

  async function doAction() {
    if (!confirm || !os) return;

    setConfirmLoading(true);
    try {
      if (confirm.type === "finalizar") await api.post(`/api/os/${os.id}/finalizar`);
      if (confirm.type === "faturar") await api.post(`/api/os/${os.id}/faturar`);
      if (confirm.type === "cancelar") await api.post(`/api/os/${os.id}/cancelar`);

      toast.success("Ação concluída ✅");
      setConfirm(null);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao executar ação");
    } finally {
      setConfirmLoading(false);
    }
  }

  async function removeItem(itemId: string) {
    if (!os) return;
    if (!window.confirm("Remover este item da OS?")) return;


    try {
      await api.delete(`/api/os/${os.id}/itens/${itemId}`);
      toast.success("Item removido ✅");
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao remover item");
    }
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* topo */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900">
                OS {os ? os.id.slice(-6).toUpperCase() : ""}
              </h1>
              {os ? statusChip(os.status) : null}
            </div>
            <p className="text-sm text-slate-500">
              {os ? `Criada em: ${new Date(os.createdAt).toLocaleString("pt-BR")}` : "Carregando..."}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/os">
              <Button variant="outline" className="gap-2">
                <ArrowLeft size={16} />
                Voltar
              </Button>
            </Link>

            {canEdit && (
              <>
                <Button variant="outline" className="gap-2" onClick={() => setOpenAddItem(true)}>
                  <Plus size={16} />
                  Adicionar item
                </Button>

                <Button className="gap-2" onClick={() => setOpenAddPecaQuick(true)}>
                  <PackagePlus size={16} />
                  Atalho peça
                </Button>
              </>
            )}
          </div>
        </div>

        {/* conteúdo */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          {loading ? (
            <div className="flex items-center justify-center py-14 text-slate-500 gap-2">
              <Loader2 className="animate-spin" size={18} />
              Carregando...
            </div>
          ) : !os ? (
            <div className="py-14 text-center text-slate-500">OS não encontrada.</div>
          ) : (
            <div className="space-y-6">
              {/* Cabeçalho cliente/veículo/total */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Cliente</div>
                  <div className="font-semibold text-slate-900">{os.cliente?.nome ?? "—"}</div>
                  <div className="text-sm text-slate-600 mt-1">
                    {os.cliente?.telefone ? `Tel: ${os.cliente.telefone}` : ""}
                    {os.cliente?.documento ? ` • Doc: ${os.cliente.documento}` : ""}
                  </div>
                  {os.cliente?.email && <div className="text-sm text-slate-600">{os.cliente.email}</div>}
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Veículo</div>
                  <div className="font-semibold text-slate-900">
                    {os.veiculo?.placa ? os.veiculo.placa : "—"}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    {(os.veiculo?.marca ?? "")} {(os.veiculo?.modelo ?? "")} {os.veiculo?.ano ? `(${os.veiculo.ano})` : ""}
                  </div>
                  {os.veiculo?.km !== undefined && os.veiculo?.km !== null && (
                    <div className="text-sm text-slate-600">KM: {String(os.veiculo.km)}</div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Total</div>
                  <div className="text-2xl font-bold text-slate-900">
                    {moneyBRL(totalCalcOS)}
                  </div>


                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      className="gap-2"
                      disabled={!canEdit}
                      onClick={() => askAction("finalizar")}
                    >
                      <CheckCircle2 size={16} />
                      Finalizar
                    </Button>

                    <Button
                      variant="outline"
                      className="gap-2"
                      disabled={os.status !== "finalizada"}
                      onClick={() => askAction("faturar")}
                    >
                      <Receipt size={16} />
                      Faturar
                    </Button>

                    <Button
                      variant="destructive"
                      className="gap-2"
                      disabled={os.status === "faturada" || os.status === "cancelada"}
                      onClick={() => askAction("cancelar")}
                    >
                      <Ban size={16} />
                      Cancelar
                    </Button>



                    {canPrint(os.status) && (
                      <Button variant="outline" className="gap-2" onClick={handlePrint}>
                        <Printer size={16} />
                        Imprimir
                      </Button>
                    )}


                  </div>
                </div>
              </div>

              {/* Observações */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Observação do cliente</div>
                  <div className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">
                    {os.observacaoCliente ?? "—"}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Diagnóstico</div>
                  <div className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">
                    {os.diagnostico ?? "—"}
                  </div>
                </div>
              </div>

              {/* Itens */}
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">Itens</div>
                    <div className="text-sm text-slate-500">{os.itens.length} item(ns)</div>
                  </div>
                </div>

                {os.itens.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">Nenhum item ainda.</div>
                ) : (
                  <div className="divide-y">
                    {itensComTotal.map((it) => (
                      <div key={it.id} className="p-4 flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="text-sm text-slate-500">
                            {it.tipo === "peca" ? "Peça" : "Serviço"} • Qtd: <b className="text-slate-700">{String(it.qtd)}</b>
                          </div>
                          <div className="font-medium text-slate-900">{it.descricao}</div>
                          <div className="text-xs text-slate-500">
                            Unit: {moneyBRL(it.valorUnit)} • Total:{" "}
                            <b className="text-slate-700">{moneyBRL((it as any)._totalCalc)}</b>
                          </div>
                        </div>

                        {canEdit ? (
                          <Button
                            variant="outline"
                            className="gap-2"
                            onClick={() => removeItem(it.id)}
                            title="Remover item"
                          >
                            <Trash2 size={16} />
                            Remover
                          </Button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Nota (placeholder informativo) */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <b>Nota:</b>{" "}
                {os.nota
                  ? `Status: ${os.nota.status} ${os.nota.numero ? `• Nº ${os.nota.numero}` : ""}`
                  : "Ainda não emitida."}
              </div>
            </div>
          )}
        </div>

        {/* Modais */}
        {os?.id && (
          <>
            <AddItemModal
              open={openAddItem}
              osId={os.id}
              onClose={() => setOpenAddItem(false)}
              onCreated={load}
            />

            <AddPecaQuickModal
              open={openAddPecaQuick}
              osId={os.id}
              onClose={() => setOpenAddPecaQuick(false)}
              onCreated={load}
            />
          </>
        )}

        <ConfirmModal
          open={!!confirm}
          title={confirm?.title ?? ""}
          description={confirm?.desc ?? ""}
          confirmText={confirm?.type === "cancelar" ? "Cancelar OS" : "Confirmar"}
          variant={confirm?.type === "cancelar" ? "destructive" : "default"}
          loading={confirmLoading}
          onClose={() => setConfirm(null)}
          onConfirm={doAction}
        />
      </div>
    </AppLayout>
  );
}
